import ROUTE_PATHS from '../router/route-paths';

export const ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
};

/** What the panel shows for a role. The stored userType stays `manager`. */
export const ROLE_LABELS = {
  [ROLES.ADMIN]: 'Admin',
  [ROLES.MANAGER]: 'Intern',
};

export const getRoleLabel = (userOrRole) => {
  const role = typeof userOrRole === 'string' ? userOrRole : getUserRole(userOrRole);
  return ROLE_LABELS[role] || role;
};

/**
 * Feature keys a full admin can grant to a restricted (manager) account.
 * Mirrors ADMIN_PERMISSIONS in the backend's admin.constants.js — the API
 * enforces the same keys, the panel only uses them to hide what the account
 * cannot use anyway.
 */
export const PERMISSIONS = {
  KARAOKE: 'karaoke',
  AUDIO: 'audio',
  CHALLENGES: 'challenges',
  CATEGORIES: 'categories',
  THEMES: 'themes',
  POLLS: 'polls',
  CONTESTS: 'contests',
  BADGES: 'badges',
  CALENDAR: 'calendar',
  REPORTS: 'reports',
  APPEALS: 'appeals',
  FEEDBACK: 'feedback',
  NOTIFICATIONS: 'notifications',
  MESSAGES: 'messages',
  PATCH_UPDATES: 'patch-updates',
  PAGENT_POSTS: 'pagent-posts',
  VIEW_ACTIVITY: 'view-activity',
};

/**
 * Grantable permissions with labels, grouped the way the sidebar is grouped,
 * for the Admin Accounts form. VIEW_ACTIVITY is listed separately by the form.
 */
export const PERMISSION_GROUPS = [
  {
    id: 'overview',
    label: 'Overview',
    items: [
      { key: PERMISSIONS.CALENDAR, label: 'Calendar', description: 'Schedule overview of challenges, contests and songs' },
    ],
  },
  {
    id: 'content',
    label: 'Content',
    items: [
      { key: PERMISSIONS.KARAOKE, label: 'Karaoke', description: 'Upload songs, sync lyrics, assign lines' },
      { key: PERMISSIONS.AUDIO, label: 'Audio', description: 'Audio library' },
      { key: PERMISSIONS.CATEGORIES, label: 'Categories', description: 'Edit category details' },
    ],
  },
  {
    id: 'engagement',
    label: 'Engagement',
    items: [
      { key: PERMISSIONS.CHALLENGES, label: 'Challenges', description: 'Create and schedule challenges (start / end dates)' },
      { key: PERMISSIONS.CONTESTS, label: 'Contests' },
      { key: PERMISSIONS.THEMES, label: 'Themes' },
      { key: PERMISSIONS.POLLS, label: 'Naming Polls' },
      { key: PERMISSIONS.BADGES, label: 'Badges' },
    ],
  },
  {
    id: 'support',
    label: 'Support',
    items: [
      { key: PERMISSIONS.REPORTS, label: 'Reports' },
      { key: PERMISSIONS.APPEALS, label: 'Appeals' },
      { key: PERMISSIONS.FEEDBACK, label: 'Feedback' },
    ],
  },
  {
    id: 'notifications',
    label: 'Notifications',
    items: [
      { key: PERMISSIONS.NOTIFICATIONS, label: 'Notifications' },
      { key: PERMISSIONS.MESSAGES, label: 'Messages' },
      { key: PERMISSIONS.PATCH_UPDATES, label: 'Patch Updates' },
      { key: PERMISSIONS.PAGENT_POSTS, label: '@pagent Posts' },
    ],
  },
];

export const VIEW_ACTIVITY_PERMISSION = {
  key: PERMISSIONS.VIEW_ACTIVITY,
  label: 'Can view submission counts & activity',
  description:
    'Shows post / submission / entry counts on the pages this account can access. Off by default.',
};

export const PERMISSION_LABELS = Object.fromEntries([
  ...PERMISSION_GROUPS.flatMap((group) => group.items.map((item) => [item.key, item.label])),
  [VIEW_ACTIVITY_PERMISSION.key, 'View activity'],
]);

/**
 * What a manager could reach before per-account permissions existed. Used
 * when a manager profile arrives without an adminPermissions list (older
 * backend or a cached login payload), so nobody is locked out mid-session.
 */
export const LEGACY_MANAGER_PERMISSIONS = [
  PERMISSIONS.KARAOKE,
  PERMISSIONS.AUDIO,
  PERMISSIONS.CHALLENGES,
  PERMISSIONS.CATEGORIES,
  PERMISSIONS.THEMES,
  PERMISSIONS.POLLS,
  PERMISSIONS.CONTESTS,
  PERMISSIONS.BADGES,
  PERMISSIONS.REPORTS,
  PERMISSIONS.APPEALS,
  PERMISSIONS.FEEDBACK,
  PERMISSIONS.NOTIFICATIONS,
  PERMISSIONS.MESSAGES,
  PERMISSIONS.PATCH_UPDATES,
  PERMISSIONS.PAGENT_POSTS,
];

const stripSlash = (path) => String(path || '').replace(/^\//, '');

/** Routes every signed-in admin-panel account may open. */
const ALWAYS_ALLOWED_ROUTES = [
  '',
  stripSlash(ROUTE_PATHS.DASHBOARD),
  stripSlash(ROUTE_PATHS.CHANGE_PASSWORD),
];

/**
 * Route name (no leading slash) -> feature key needed to open it. Routes not
 * listed here are admin-only. Nested routes inherit from the closest listed
 * parent (e.g. manage-karaoke/upload -> manage-karaoke).
 */
export const ROUTE_FEATURES = {
  [stripSlash(ROUTE_PATHS.CALENDAR)]: PERMISSIONS.CALENDAR,
  [stripSlash(ROUTE_PATHS.MANAGE_KARAOKE)]: PERMISSIONS.KARAOKE,
  [stripSlash(ROUTE_PATHS.MANAGE_AUDIO)]: PERMISSIONS.AUDIO,
  [stripSlash(ROUTE_PATHS.MANAGE_CATEGORY)]: PERMISSIONS.CATEGORIES,
  [stripSlash(ROUTE_PATHS.MANAGE_CHALLENGE)]: PERMISSIONS.CHALLENGES,
  [stripSlash(ROUTE_PATHS.MANAGE_CONTESTS)]: PERMISSIONS.CONTESTS,
  [stripSlash(ROUTE_PATHS.MANAGE_THEME)]: PERMISSIONS.THEMES,
  [stripSlash(ROUTE_PATHS.MANAGE_POLLS)]: PERMISSIONS.POLLS,
  [stripSlash(ROUTE_PATHS.MANAGE_BADGE)]: PERMISSIONS.BADGES,
  [stripSlash(ROUTE_PATHS.MANAGE_TICKET)]: PERMISSIONS.REPORTS,
  [stripSlash(ROUTE_PATHS.MANAGE_APPEALS)]: PERMISSIONS.APPEALS,
  [stripSlash(ROUTE_PATHS.MANAGE_FEEDBACK)]: PERMISSIONS.FEEDBACK,
  [stripSlash(ROUTE_PATHS.MANAGE_NOTIFICATIONS)]: PERMISSIONS.NOTIFICATIONS,
  [stripSlash(ROUTE_PATHS.MANAGE_MESSAGES)]: PERMISSIONS.MESSAGES,
  [stripSlash(ROUTE_PATHS.MANAGE_PATCH_UPDATES)]: PERMISSIONS.PATCH_UPDATES,
  [stripSlash(ROUTE_PATHS.MANAGE_PAGENT_POSTS)]: PERMISSIONS.PAGENT_POSTS,
};

/** Feature key required for a route, walking up nested segments. */
export const getRouteFeature = (route) => {
  const routeName = stripSlash(route);
  if (ROUTE_FEATURES[routeName]) return ROUTE_FEATURES[routeName];
  const parts = routeName.split('/');
  for (let i = parts.length - 1; i > 0; i--) {
    const parent = parts.slice(0, i).join('/');
    if (ROUTE_FEATURES[parent]) return ROUTE_FEATURES[parent];
  }
  return null;
};

// Get user role from user details
export const getUserRole = (userDetails) => {
  return userDetails?.userType || ROLES.MANAGER; // Default to manager for safety
};

export const isAdmin = (userRole) => userRole === ROLES.ADMIN;

export const isManager = (userRole) => userRole === ROLES.MANAGER;

/**
 * Effective permission list for a profile. Admins have everything; managers
 * have exactly their adminPermissions (legacy set when absent); anything else
 * has nothing.
 */
export const getEffectivePermissions = (userDetails) => {
  const role = getUserRole(userDetails);
  if (isAdmin(role)) return Object.values(PERMISSIONS);
  if (!isManager(role)) return [];
  if (Array.isArray(userDetails?.adminPermissions)) return userDetails.adminPermissions;
  return LEGACY_MANAGER_PERMISSIONS;
};

export const hasFeature = (userDetails, key) => {
  if (isAdmin(getUserRole(userDetails))) return true;
  return getEffectivePermissions(userDetails).includes(key);
};

export const canViewActivity = (userDetails) => hasFeature(userDetails, PERMISSIONS.VIEW_ACTIVITY);

const isAlwaysAllowed = (route) => {
  const routeName = stripSlash(route);
  if (ALWAYS_ALLOWED_ROUTES.includes(routeName)) return true;
  // nested under an always-allowed route (none today, kept for safety)
  const parts = routeName.split('/');
  for (let i = parts.length - 1; i > 0; i--) {
    if (ALWAYS_ALLOWED_ROUTES.includes(parts.slice(0, i).join('/'))) return true;
  }
  return false;
};

/**
 * Check if a profile may open a route.
 *
 * Accepts either the user details object (preferred) or a bare role string
 * for older call sites; a bare 'manager' string is treated as a legacy manager.
 */
export const hasPermission = (userOrRole, route) => {
  const user = typeof userOrRole === 'string' ? { userType: userOrRole } : userOrRole;
  const role = getUserRole(user);

  // Admin has access to everything
  if (isAdmin(role)) return true;

  if (!user || !isManager(role)) return false;

  if (isAlwaysAllowed(route)) return true;

  const feature = getRouteFeature(route);
  if (!feature) return false; // admin-only route
  return hasFeature(user, feature);
};

// Filter a flat list of sidebar items based on the profile
export const filterSidebarItems = (sidebarItems, userOrRole) => {
  if (!userOrRole) return [];
  return sidebarItems.filter((item) => hasPermission(userOrRole, item.path));
};

// Filter grouped sidebar config; drop groups with zero permitted items
export const filterSidebarGroups = (groups, userOrRole) => {
  if (!userOrRole) return [];

  return groups
    .map((group) => ({
      ...group,
      items: filterSidebarItems(group.items, userOrRole),
    }))
    .filter((group) => group.items.length > 0);
};
